import React, { useState } from "react";
import { Users, UserX, UserPlus, Sparkles, Layers, BarChart3, Search } from "lucide-react";
import { useTranslation, useFormat } from "../../../i18n";
import { useAnalyticsSection } from "../../analytics/hooks";
import { KpiTile, ChartCard, Segmented, Skeleton, ErrorState, DataTable, PersonCell, ShareBar, EmptyState } from "../../analytics/primitives";
import { Histogram } from "../../analytics/charts";
import { SERIES, STATUS } from "../../analytics/palette";

const rateColor = (rate) => (rate === null || rate === undefined ? STATUS.info : rate >= 70 ? STATUS.good : rate >= 40 ? STATUS.warning : STATUS.critical);

const PeopleTable = ({ users, mode, search }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const filtered = search ? users.filter((u) => `${u.full_name || ""} ${u.email || ""} ${u.department || ""} ${(u.teams || []).join(" ")}`.toLowerCase().includes(search.toLowerCase())) : users;
  const dateCol = mode === "received" ? "last_received_at" : "last_given_at";
  const daysCol = mode === "received" ? "days_since_received" : "days_since_given";
  return (
    <DataTable
      keyField="id"
      rows={filtered}
      pageSize={15}
      emptyMessage={t("participation.nobody_missing")}
      columns={[
        { key: "full_name", label: t("table.name"), render: (u) => <PersonCell user={u} sub={u.job_title || u.department} />, sortValue: (u) => (u.full_name || u.email || "").toLowerCase() },
        { key: "teams", label: t("table.teams"), render: (u) => <span className="text-xs text-slate-600">{(u.teams || []).join(", ") || "—"}</span>, sortValue: (u) => (u.teams || []).join(",") },
        { key: "hire_date", label: t("table.hired"), render: (u) => <span className="text-xs text-slate-600">{u.hire_date ? fmt.date(new Date(`${u.hire_date}T00:00:00`), { month: "short", year: "numeric" }) : "—"}</span> },
        {
          key: daysCol,
          label: mode === "received" ? t("table.last_recognised") : t("table.last_gave"),
          align: "right",
          sortValue: (u) => (u[daysCol] === null ? Number.MAX_SAFE_INTEGER : u[daysCol]),
          render: (u) =>
            u[dateCol] ? (
              <span className={`text-xs font-medium ${u[daysCol] > 90 ? "text-rose-600" : u[daysCol] > 30 ? "text-amber-600" : "text-slate-700"}`}>{t("days_ago", { count: u[daysCol] })}</span>
            ) : (
              <span className="text-xs font-semibold text-rose-600">{t("never")}</span>
            ),
        },
        { key: mode === "received" ? "given_in_period" : "received_in_period", label: mode === "received" ? t("table.gave_in_period") : t("table.received_in_period"), align: "right", render: (u) => fmt.number(mode === "received" ? u.given_in_period : u.received_in_period) },
      ]}
      initialSort={{ key: daysCol, dir: "desc" }}
    />
  );
};

const ParticipationTab = ({ filters }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const q = useAnalyticsSection("participation", filters);
  const [distMode, setDistMode] = useState("received");
  const [peopleMode, setPeopleMode] = useState("received");
  const [search, setSearch] = useState("");
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (q.isLoading || !q.data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={92} />
          ))}
        </div>
        <Skeleton height={280} />
      </div>
    );
  }
  const d = q.data;
  const s = d.summary;
  const pctFmt = (v) => (v === null || v === undefined ? "—" : fmt.number(v / 100, { style: "percent", maximumFractionDigits: 0 }));
  const prevRate = d.previous?.participation_rate;
  const rateMetric = prevRate !== undefined && prevRate !== null && s.participation_rate !== null ? { value: s.participation_rate, previous: prevRate, delta: Math.round((s.participation_rate - prevRate) * 10) / 10, delta_pct: Math.round((s.participation_rate - prevRate) * 10) / 10 } : undefined;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <KpiTile icon={Users} label={t("participation.rate")} value={pctFmt(s.participation_rate)} metric={rateMetric} progress={s.participation_rate} tone="blue" sub={t("kpi.of_members", { participants: fmt.number(s.participants), headcount: fmt.number(s.headcount) })} />
        <KpiTile icon={Users} label={t("participation.recipient_rate")} value={pctFmt(s.recipient_rate)} progress={s.recipient_rate} tone="pink" sub={t("participation.n_people", { count: s.recipients })} />
        <KpiTile icon={Users} label={t("participation.sender_rate")} value={pctFmt(s.sender_rate)} progress={s.sender_rate} tone="teal" sub={t("participation.n_people", { count: s.senders })} />
        <KpiTile icon={UserX} label={t("participation.unrecognised")} value={fmt.number(d.unrecognised.total)} tone={d.unrecognised.total ? "red" : "green"} sub={t("participation.never_count", { count: d.unrecognised.never })} />
        <KpiTile icon={UserPlus} label={t("participation.new_joiners")} value={`${fmt.number(d.new_joiners.recognised)} / ${fmt.number(d.new_joiners.total)}`} tone="green" sub={t("participation.new_joiners_sub")} hint={t("participation.new_joiners_hint")} />
        <KpiTile icon={Sparkles} label={t("participation.concentration")} value={pctFmt(d.concentration.top_10pct_share)} tone="yellow" sub={t("participation.gini", { value: d.concentration.gini_received === null ? "—" : fmt.number(d.concentration.gini_received, { maximumFractionDigits: 2 }) })} hint={t("participation.concentration_hint")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title={t("participation.distribution_title")}
          subtitle={t("participation.distribution_subtitle")}
          icon={BarChart3}
          actions={<Segmented ariaLabel={t("chart_mode")} value={distMode} onChange={setDistMode} options={[{ value: "received", label: t("participation.received") }, { value: "given", label: t("participation.given") }]} />}
        >
          <Histogram data={distMode === "received" ? d.received_distribution : d.given_distribution} labelKey="bucket" valueKey="users" color={distMode === "received" ? SERIES[0] : SERIES[3]} />
        </ChartCard>
        <ChartCard title={t("participation.tenure_title")} subtitle={t("participation.tenure_subtitle")} icon={Layers}>
          {d.by_tenure.bands.every((b) => b.members === 0) ? (
            <EmptyState message={t("participation.no_hire_dates")} height={200} />
          ) : (
            <div className="space-y-2">
              {d.by_tenure.bands.map((b) => (
                <div key={b.band} className="flex items-center gap-3 text-xs">
                  <span className="w-20 text-slate-600">{t(`tenure.${b.band}`)}</span>
                  <span className="w-12 text-right tabular-nums text-slate-500">{fmt.number(b.members)}</span>
                  <div className="flex-1">
                    <ShareBar value={b.recipient_rate || 0} color={rateColor(b.recipient_rate)} label={pctFmt(b.recipient_rate)} />
                  </div>
                  <span className="w-14 text-right tabular-nums text-slate-500" title={t("participation.received_per_member")}>
                    {b.received_per_member === null ? "—" : fmt.number(b.received_per_member, { maximumFractionDigits: 1 })}
                  </span>
                </div>
              ))}
              <p className="text-[10px] text-slate-400 pt-1">{t("participation.tenure_legend", { unknown: d.by_tenure.unknown })}</p>
            </div>
          )}
        </ChartCard>
      </div>

      <ChartCard title={t("participation.by_team_title")} subtitle={t("participation.by_team_subtitle")} icon={Users}>
        <DataTable
          keyField="team_id"
          rows={d.by_team}
          initialSort={{ key: "participation_rate", dir: "desc" }}
          emptyMessage={t("participation.no_teams")}
          columns={[
            { key: "name", label: t("table.team") },
            { key: "members", label: t("table.members"), align: "right" },
            { key: "participation_rate", label: t("participation.rate"), render: (r) => <ShareBar value={r.participation_rate || 0} color={rateColor(r.participation_rate)} label={pctFmt(r.participation_rate)} /> },
            { key: "recipient_rate", label: t("participation.recipient_rate"), align: "right", render: (r) => pctFmt(r.recipient_rate) },
            { key: "sender_rate", label: t("participation.sender_rate"), align: "right", render: (r) => pctFmt(r.sender_rate) },
            { key: "received", label: t("table.received"), align: "right" },
            { key: "given", label: t("table.given"), align: "right" },
            { key: "received_per_member", label: t("table.per_member"), align: "right", render: (r) => fmt.number(r.received_per_member, { maximumFractionDigits: 1 }) },
            { key: "points_received", label: t("table.points"), align: "right", render: (r) => fmt.number(r.points_received) },
            { key: "unrecognised", label: t("participation.unrecognised"), align: "right", render: (r) => <span className={r.unrecognised ? "text-rose-600 font-medium" : ""}>{r.unrecognised}</span> },
          ]}
        />
      </ChartCard>

      <ChartCard title={t("participation.by_department_title")} icon={Layers}>
        <DataTable
          keyField="name"
          rows={d.by_department}
          initialSort={{ key: "participation_rate", dir: "desc" }}
          columns={[
            { key: "name", label: t("table.department") },
            { key: "members", label: t("table.members"), align: "right" },
            { key: "participation_rate", label: t("participation.rate"), render: (r) => <ShareBar value={r.participation_rate || 0} color={rateColor(r.participation_rate)} label={pctFmt(r.participation_rate)} /> },
            { key: "recipient_rate", label: t("participation.recipient_rate"), align: "right", render: (r) => pctFmt(r.recipient_rate) },
            { key: "received", label: t("table.received"), align: "right" },
            { key: "given", label: t("table.given"), align: "right" },
            { key: "received_per_member", label: t("table.per_member"), align: "right", render: (r) => fmt.number(r.received_per_member, { maximumFractionDigits: 1 }) },
            { key: "unrecognised", label: t("participation.unrecognised"), align: "right" },
          ]}
        />
      </ChartCard>

      <ChartCard
        title={peopleMode === "received" ? t("participation.missing_title") : t("participation.silent_title")}
        subtitle={peopleMode === "received" ? t("participation.missing_subtitle", { count: d.unrecognised.total, share: pctFmt(d.unrecognised.share) }) : t("participation.silent_subtitle", { count: d.silent_senders.total, share: pctFmt(d.silent_senders.share) })}
        icon={UserX}
        actions={
          <>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search_people")} aria-label={t("search_people")} className="pl-7 pr-2 py-1 text-xs border border-slate-200 rounded-full w-40 focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <Segmented ariaLabel={t("chart_mode")} value={peopleMode} onChange={setPeopleMode} options={[{ value: "received", label: t("participation.not_recognised") }, { value: "given", label: t("participation.not_giving") }]} />
          </>
        }
      >
        <PeopleTable users={peopleMode === "received" ? d.unrecognised.users : d.silent_senders.users} mode={peopleMode} search={search} />
        {(peopleMode === "received" ? d.unrecognised.total : d.silent_senders.total) > 100 && <p className="text-[11px] text-slate-400 mt-2">{t("participation.showing_first", { count: 100 })}</p>}
      </ChartCard>

      {d.new_joiners.total > 0 && (
        <ChartCard title={t("participation.new_joiners_title")} subtitle={t("participation.new_joiners_table_subtitle", { rate: pctFmt(d.new_joiners.recognised_rate) })} icon={UserPlus}>
          <DataTable
            keyField="id"
            rows={d.new_joiners.users}
            pageSize={10}
            columns={[
              { key: "full_name", label: t("table.name"), render: (u) => <PersonCell user={u} sub={u.job_title || u.department} />, sortValue: (u) => (u.full_name || "").toLowerCase() },
              { key: "hire_date", label: t("table.hired"), render: (u) => <span className="text-xs text-slate-600">{u.hire_date ? fmt.date(new Date(`${u.hire_date}T00:00:00`)) : "—"}</span> },
              { key: "teams", label: t("table.teams"), render: (u) => <span className="text-xs text-slate-600">{(u.teams || []).join(", ") || "—"}</span>, sortable: false },
              { key: "received_in_period", label: t("table.received"), align: "right", render: (u) => <span className={u.received_in_period ? "text-emerald-700 font-medium" : "text-rose-600 font-medium"}>{fmt.number(u.received_in_period)}</span> },
              { key: "given_in_period", label: t("table.given"), align: "right" },
            ]}
          />
        </ChartCard>
      )}
    </div>
  );
};

export default ParticipationTab;
